// Phase 12.2 — shape of GET /health/ingestion (defined in
// server/src/domains/health/routes.js). Mirrors the JSON exactly so
// the dashboard banner + ops debug panel can render against a stable
// contract.

export type AgeBucket = 'fresh' | 'slow' | 'stale' | 'silent' | 'unknown';

export interface DeviceFreshnessRow {
  deviceId: string;
  hostname: string | null;
  orgId: string | null;
  agentVersion: string | null;
  os: string | null;
  lastSeenAt: string | null;
  ageMs: number | null;
  ageBucket: AgeBucket;
}

export interface IngestionHealth {
  status: AgeBucket;
  degraded: boolean;
  thresholds: { freshSec: number; staleSec: number };
  screenshots: {
    lastAt: string | null;
    ageMs: number | null;
    ageBucket: AgeBucket;
    sample: { orgId: string | null; deviceId: string | null; hostname: string | null } | null;
  };
  devices: {
    total: number;
    onlineNow: number;
    idleNow: number;
    offlineNow: number;
    lastHeartbeatAt: string | null;
    lastHeartbeatAgeMs: number | null;
    lastHeartbeatAgeBucket: AgeBucket;
    // Phase 12.3 — server enumerates each device's freshness; rows are
    // sorted oldest-lastSeen first so a "who's silent?" list renders
    // straight from this array.
    rows?: Array<DeviceFreshnessRow>;
    silentCount?: number;
  };
  perOrg: Array<{
    orgId: string;
    orgName: string | null;
    lastAt: string | null;
    ageMs: number | null;
    ageBucket: AgeBucket;
    todayCount: number;
    totalCount: number;
  }>;
}

// Human-friendly age formatter. Used by the dashboard banner copy and
// the ops debug panel. Examples: "12s", "4m", "3h", "2d".
export function formatAge(ageMs: number | null | undefined): string {
  if (ageMs == null || !Number.isFinite(ageMs)) return 'unknown';
  const s = Math.floor(ageMs / 1000);
  if (s < 60)    return `${s}s`;
  if (s < 3600)  return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
